import { inject, Injector, runInInjectionContext } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { UserService } from '../../services/user.service';
import { map, mergeMap, catchError, of, tap, switchMap, exhaustMap, forkJoin, filter, withLatestFrom } from 'rxjs';
import { completeOnboarding, deleteUserAccount, deleteUserAccountFailure, deleteUserAccountSuccess, loadAllUsers, loadAllUsersFailure, loadAllUsersSuccess, loadLinkedAccounts, loadLinkedAccountsFailure, loadLinkedAccountsSuccess, loadUser, loadUserCount, loadUserCountFailure, loadUserCountSuccess, loadUserFailure, loadUserSuccess, updateMarketplaceSubscription, updateMarketplaceSubscriptionFailure, updateMarketplaceSubscriptionSuccess, updateUserEmail, updateUserEmailFailure, updateUserEmailSuccess, updateUserOnboardingStatus, updateUserOnboardingStatusFailure, updateUserOnboardingStatusSuccess, updateUserPassword, updateUserPasswordFailure, updateUserPasswordSuccess, updateUserPreferences, updateUserPreferencesFailure, updateUserPreferencesSuccess, updateUserProfile, updateUserProfileFailure, updateUserProfileSuccess, updateUserRole, updateUserRoleFailure, updateUserRoleSuccess } from './user.actions';
import { Store } from '@ngrx/store';
import { AuthState } from '../auth/auth.reducer';
import { logout, setLoggedIn } from '../auth/auth.actions';
import { loadHouseholds } from '../household/household.actions';
import { HouseholdState } from '../household/households.reducer';
import { Router } from '@angular/router';
import { LanguageService } from '../../services/language.service';
import { MatDialog } from '@angular/material/dialog';
import { OnboardingDialogComponent } from '../../../shared/components/molecules/onboarding-dialog/onboarding-dialog.component';
import { loadPendingAnnouncement } from '../announcement/announcement.actions';
import { selectUserId } from './user.selectors';
import { NotificationService } from '../../services/notification.service';
import { LogoutType } from '../../../shared/enums/logoutType.enum';
import { TranslateService } from '@ngx-translate/core';
import { AuthTokenService } from '../../services/auth-token.service';

export const loadUser$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(loadUser),
      mergeMap(({ userId }) =>
        userService.getUserById(userId).pipe(
          map((user) => loadUserSuccess({ user })),
          catchError((error) => of(loadUserFailure({ error })))
        )
      )
    ),
  { functional: true }
);

export const loadUserSuccess$ = createEffect(
  (
    actions$ = inject(Actions),
    authStore = inject(Store<AuthState>),
    householdStore = inject(Store<HouseholdState>),
    languageService = inject(LanguageService),
    dialog = inject(MatDialog),
    injector = inject(Injector),
  ) =>
    actions$.pipe(
      ofType(loadUserSuccess),
      tap(({ user }) => {
        // 1. Dispatch related actions
        authStore.dispatch(setLoggedIn());
        householdStore.dispatch(loadHouseholds());

        // 2. Sync preferences to localStorage + i18n
        localStorage.setItem('unitSystem', user.preferred_unit_system);
        localStorage.setItem('dietary', JSON.stringify(user.preferred_dietary ?? []));
        languageService.setLanguage(user.preferred_language);

        // 3. Open onboarding if needed
        if (!user.onboarding_complete) {
          runInInjectionContext(injector, () => {
            dialog.open(OnboardingDialogComponent, {
              disableClose: true,
              width: '500px'
            });
          });
        }
      })
    ),
  { functional: true, dispatch: false }
);

export const loadAnnouncementsIfOnboarded$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(loadUserSuccess),
      filter(({ user }) => user.onboarding_complete),
      map(() => loadPendingAnnouncement())
    ),
  { functional: true }
);


export const updatePreferences$ = createEffect(
  (
    actions$ = inject(Actions),
    userService = inject(UserService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(updateUserPreferences),
      exhaustMap(action =>
        userService.updatePreferences(action).pipe(
          map(() => updateUserPreferencesSuccess({
            updatedPreferences: {
              preferred_language: action.preferred_language,
              preferred_dietary: action.preferred_dietary,
              preferred_unit_system: action.preferred_unit_system,
              preferred_timezone: action.preferred_timezone,
            },
          })),
          catchError(error => {
            translate.get('user.notifications.preferencesUpdateFailed').subscribe(msg => notification.error(msg));
            return of(updateUserPreferencesFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const updatePreferencesSuccess$ = createEffect(
  (
    actions$ = inject(Actions),
    languageService = inject(LanguageService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(updateUserPreferencesSuccess),
      tap(({ updatedPreferences }) => {
        const { preferred_language, preferred_dietary, preferred_unit_system } = updatedPreferences;
        localStorage.setItem('unitSystem', preferred_unit_system);
        localStorage.setItem('dietary', JSON.stringify(preferred_dietary ?? []));
        languageService.setLanguage(preferred_language);
        translate.get('user.notifications.preferencesUpdated').subscribe(msg => notification.success(msg));
      })
    ),
  { functional: true, dispatch: false }
);

export const updateUserEmail$ = createEffect(
  (
    actions$ = inject(Actions),
    userService = inject(UserService),
    store = inject(Store),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(updateUserEmail),
      withLatestFrom(store.select(selectUserId)),
      exhaustMap(([{ email }, userId]) =>
        userService.updateEmail(email).pipe(
          switchMap(() =>
            userService.getUserById(userId!).pipe(
              map(user => updateUserEmailSuccess({ user })),
              catchError(error => {
                translate.get('user.notifications.emailReloadFailed').subscribe(msg => notification.error(msg));
                return of(updateUserEmailFailure({ error }));
              })
            )
          ),
          catchError(error => {
            const key = error?.status === 409 ? 'user.notifications.emailTaken' : 'user.notifications.emailUpdateFailed';
            translate.get(key).subscribe(msg => notification.error(msg));
            return of(updateUserEmailFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const updateUserPassword$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService), notification = inject(NotificationService), translate = inject(TranslateService)) =>
    actions$.pipe(
      ofType(updateUserPassword),
      exhaustMap(({ currentPassword, newPassword }) =>
        userService.updatePassword({ currentPassword, newPassword }).pipe(
          map(() => {
            translate.get('user.notifications.passwordUpdated').subscribe(msg => notification.success(msg));
            return updateUserPasswordSuccess();
          }),
          catchError(error => {
            translate.get('user.notifications.passwordUpdateFailed').subscribe(msg => notification.error(msg));
            return of(updateUserPasswordFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

// updated profile (just name)
export const updateUserProfile$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService), notification = inject(NotificationService), translate = inject(TranslateService)) =>
    actions$.pipe(
      ofType(updateUserProfile),
      exhaustMap(({ name }) =>
        userService.updateProfile({ name }).pipe(
          map(user => {
            translate.get('user.notifications.nameUpdated').subscribe(msg => notification.success(msg));
            return updateUserProfileSuccess({ user });
          }),
          catchError(error => {
            translate.get('user.notifications.nameUpdateFailed').subscribe(msg => notification.error(msg));
            return of(updateUserProfileFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);


export const deleteUserAccount$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService), notification = inject(NotificationService), translate = inject(TranslateService)) =>
    actions$.pipe(
      ofType(deleteUserAccount),
      switchMap(() =>
        userService.deleteAccount().pipe(
          map(() => {
            translate.get('user.notifications.accountDeleted').subscribe(msg => notification.success(msg));
            return deleteUserAccountSuccess();
          }),
          catchError(error => {
            translate.get('user.notifications.accountDeleteFailed').subscribe(msg => notification.error(msg));
            return of(deleteUserAccountFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const onDeleteUserAccountSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(deleteUserAccountSuccess),
      map(() => logout({ source: LogoutType.Deletion }))
    ),
  { functional: true }
);

export const updateUserRole$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService), notification = inject(NotificationService), translate = inject(TranslateService)) =>
    actions$.pipe(
      ofType(updateUserRole),
      exhaustMap(({ username, role }) =>
        userService.updateUserRole(username, role).pipe(
          map(({ message }) => {
            translate.get('user.notifications.roleUpdated', { username, role }).subscribe(msg => notification.success(msg));
            return updateUserRoleSuccess({ username, role, message });
          }),
          catchError(error => {
            translate.get('user.notifications.roleUpdateFailed', { username }).subscribe(msg => notification.error(msg));
            return of(updateUserRoleFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const updateUserOnboardingStatus$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(updateUserOnboardingStatus),
      switchMap(({ onboarding_complete }) =>
        userService.updateOnboardingStatus(onboarding_complete).pipe(
          map(() => updateUserOnboardingStatusSuccess({ onboarding_complete })),
          catchError(error => of(updateUserOnboardingStatusFailure({ error })))
        )
      )
    ),
  { functional: true }
);

export const completeOnboarding$ = createEffect(
  (actions$ = inject(Actions), notification = inject(NotificationService), translate = inject(TranslateService), authTokenService = inject(AuthTokenService)) => {
    const userService = inject(UserService);
    const router = inject(Router);

    return actions$.pipe(
      ofType(completeOnboarding),
      switchMap(({ preferred_language, preferred_dietary, preferred_unit_system, onboarding_complete, name }) =>
        forkJoin([
          userService.updatePreferences({ preferred_language, preferred_dietary, preferred_unit_system }),
          userService.updateProfile({ name }),
          userService.updateOnboardingStatus(onboarding_complete),
        ]).pipe(
          tap(() => {
            const redirectUrl = authTokenService.getRedirectUrl();
            if (redirectUrl) {
              router.navigateByUrl(redirectUrl);
              authTokenService.clearRedirectUrl();
            } else {
              router.navigate(['/meal-planner']);
            }
          }),
          mergeMap(([_, updatedUser, __]) => [
            updateUserPreferencesSuccess({
              updatedPreferences: { preferred_language, preferred_dietary, preferred_unit_system }
            }),
            updateUserProfileSuccess({ user: updatedUser }),
            updateUserOnboardingStatusSuccess({ onboarding_complete }),
          ]),
          catchError(error => {
            translate.get('user.notifications.onboardingFailed').subscribe(msg => notification.success(msg));
            return of(updateUserOnboardingStatusFailure({ error }));
          })
        )
      )
    );
  },
  { functional: true }
);

export const onboardingSuccessNotification$ = createEffect(
  (actions$ = inject(Actions), notification = inject(NotificationService), translate = inject(TranslateService)) =>
    actions$.pipe(
      ofType(updateUserOnboardingStatusSuccess),
      tap(() => {
        translate.get('user.notifications.onboardingComplete').subscribe(msg => notification.success(msg));
      })
    ),
  { functional: true, dispatch: false }
);

export const loadUserCount$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(loadUserCount),
      switchMap(() =>
        userService.getUserCount().pipe(
          map(({ count }) => loadUserCountSuccess({ count })),
          catchError(error => of(loadUserCountFailure({ error })))
        )
      )
    ),
  { functional: true }
);

export const loadAllUsers$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(loadAllUsers),
      switchMap(() =>
        userService.getAllUsers().pipe(
          map((users) => loadAllUsersSuccess({ users })),
          catchError(error => of(loadAllUsersFailure({ error })))
        )
      )
    ),
  { functional: true }
);

export const updateMarketplaceSubscription$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService), notification = inject(NotificationService), translate = inject(TranslateService)) =>
    actions$.pipe(
      ofType(updateMarketplaceSubscription),
      exhaustMap(({ subscribed }) =>
        userService.updateMarketplaceSubscription(subscribed).pipe(
          map(() => {
            const marketplace_subscribed_at = subscribed ? new Date().toISOString() : null;
            const notificationKey = subscribed ? 'marketplace.notifications.subscribeSuccess' : 'marketplace.notifications.unsubscribeSuccess';
            translate.get(notificationKey).subscribe(msg => notification.success(msg));
            return updateMarketplaceSubscriptionSuccess({ marketplace_subscribed: subscribed, marketplace_subscribed_at });
          }),
          catchError(error => {
            translate.get('marketplace.notifications.subscribeError').subscribe(msg => notification.error(msg));
            return of(updateMarketplaceSubscriptionFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const loadLinkedAccounts$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(loadLinkedAccounts),
      switchMap(() =>
        userService.getLinkedAccounts().pipe(
          map((accounts) => loadLinkedAccountsSuccess({ accounts })),
          catchError((error) => of(loadLinkedAccountsFailure({ error })))
        )
      )
    ),
  { functional: true }
);

export const loadLinkedAccountsOnLogin$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(loadUserSuccess),
      map(() => loadLinkedAccounts())
    ),
  { functional: true }
);


export const UserEffects = {
    loadUser$,
    loadUserSuccess$,
    updatePreferences$,
    updatePreferencesSuccess$,
    updateUserProfile$,
    updateUserEmail$,
    updateUserPassword$,
    deleteUserAccount$,
    onDeleteUserAccountSuccess$,
    updateUserRole$,
    updateUserOnboardingStatus$ ,
    completeOnboarding$,
    loadAnnouncementsIfOnboarded$,
    onboardingSuccessNotification$,
    loadUserCount$,
    loadAllUsers$,
    updateMarketplaceSubscription$,
    loadLinkedAccounts$,
    loadLinkedAccountsOnLogin$,
  };